import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'

import { type CivilDate, compareCivilDate } from '~/models/civilDate'
import type { VoltageClass } from '~/models/voltageBreakdown'
import type {
  ContractStage,
  ContractStatus,
  CustomerSummary,
  SupplyPoint,
} from '~/pages/CustomerInfo/models/types'

/** 電圧階級の選択肢 (表示順: 特別高圧 → 高圧 → 低圧)。 */
export const VOLTAGE_CLASSES: readonly VoltageClass[] = [
  'extraHigh',
  'high',
  'low',
]

export const VOLTAGE_I18N_KEY: Record<VoltageClass, string> = {
  extraHigh: 'page:customerInfo.voltage.extraHigh',
  high: 'page:customerInfo.voltage.high',
  low: 'page:customerInfo.voltage.low',
}

/** 区分の絞り込み。 */
export type StageFilter = 'all' | ContractStage

export const STAGE_FILTERS: readonly StageFilter[] = [
  'all',
  'contracted',
  'prospect',
  'lost',
]

export const STAGE_FILTER_I18N_KEY: Record<StageFilter, string> = {
  all: 'page:customerInfo.stageFilter.all',
  contracted: 'page:customerInfo.stageFilter.contracted',
  prospect: 'page:customerInfo.stageFilter.prospect',
  lost: 'page:customerInfo.stageFilter.lost',
}

export const STATUS_I18N_KEY: Record<ContractStatus, string> = {
  prospect: 'page:customerInfo.status.prospect',
  lost: 'page:customerInfo.status.lost',
  supplying: 'page:customerInfo.status.supplying',
  starting: 'page:customerInfo.status.starting',
  ending: 'page:customerInfo.status.ending',
  ended: 'page:customerInfo.status.ended',
}

/**
 * 状態 (AC-CIN-027)。 区分が契約の時だけ日付と今日 (JST) から導出する。
 * 供給開始日が今日より後 = 開始予定、 供給終了日が今日より前 = 終了、
 * 供給終了日があり今日以降 = 解約予定、 それ以外 = 供給中。
 */
export const deriveContractStatus = (
  point: Pick<SupplyPoint, 'stage' | 'supplyStartDate' | 'supplyEndDate'>,
  today: CivilDate,
): ContractStatus => {
  if (point.stage === 'prospect') return 'prospect'
  if (point.stage === 'lost') return 'lost'
  if (compareCivilDate(point.supplyStartDate, today) > 0) return 'starting'
  if (
    point.supplyEndDate !== undefined &&
    compareCivilDate(point.supplyEndDate, today) < 0
  ) {
    return 'ended'
  }
  if (point.supplyEndDate !== undefined) return 'ending'
  return 'supplying'
}

/** 契約一覧の並び順: 供給開始日の新しい順 → 需要家名 (AC-CIN-027)。 */
export const compareSupplyPointsForList = (
  a: SupplyPoint,
  b: SupplyPoint,
): number => {
  const byStart = compareCivilDate(b.supplyStartDate, a.supplyStartDate)
  if (byStart !== 0) return byStart
  return a.customerName.localeCompare(b.customerName, 'ja')
}

export const matchesStageFilter = (
  point: Pick<SupplyPoint, 'stage'>,
  filter: StageFilter,
): boolean => filter === 'all' || point.stage === filter

/** 顧客一覧の絞り込み (AC-CIN-025)。 エリアで絞ると地点 0 件の需要家は出ない。 */
export const matchesCustomerFilter = (
  customer: CustomerSummary,
  filter: {
    areaCode: AreaCode | undefined
    voltageClass: VoltageClass | undefined
    search: string
  },
): boolean => {
  if (
    filter.areaCode !== undefined &&
    customer.areaCodes[0] !== filter.areaCode
  ) {
    return false
  }
  if (
    filter.voltageClass !== undefined &&
    !customer.voltageClasses.includes(filter.voltageClass)
  ) {
    return false
  }
  const keyword = filter.search.trim()
  // 需要家名・契約者名のどちらでもヒットさせる (大文字小文字は区別しない)
  const lower = keyword.toLowerCase()
  return (
    keyword === '' ||
    customer.customerName.toLowerCase().includes(lower) ||
    customer.contractorName.toLowerCase().includes(lower)
  )
}

export const formatKw = (kw: number): string =>
  kw.toLocaleString('ja-JP', { maximumFractionDigits: 3 })

/** Decimal 文字列の kW を表示用に整える (表示だけ number にする。 編集・送信は元の文字列を使う)。 */
export const formatKwDecimal = (kw: string): string => formatKw(Number(kw))

/** 22 桁の ASCII 数字か。 */
export const isValidSupplyPointNumber = (value: string): boolean =>
  /^[0-9]{22}$/.test(value)
